import React, { useContext } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import type { DeveloperGuideSection } from "../../types";
import DeveloperGuideCodeSampleEditor from "./DeveloperGuideCodeSampleEditor";
import { EnhanceWithAiContext } from "./EnhanceWithAiDrawer";

interface DeveloperGuideSectionEditorProps {
  sections: DeveloperGuideSection[];
  onChange: (sections: DeveloperGuideSection[]) => void;
  guideName: string;
  linkedApiUuids: string[];
  disabled?: boolean;
}

function newSection(sortOrder: number): DeveloperGuideSection {
  return {
    id: crypto.randomUUID(),
    sortOrder,
    title: "",
    instructions: "",
    codeSamples: [],
  };
}

const DeveloperGuideSectionEditor: React.FC<DeveloperGuideSectionEditorProps> = ({
  sections,
  onChange,
  guideName,
  linkedApiUuids,
  disabled,
}) => {
  const ai = useContext(EnhanceWithAiContext);

  const updateSection = (index: number, patch: Partial<DeveloperGuideSection>) => {
    onChange(
      sections.map((section, i) =>
        i === index ? { ...section, ...patch } : section,
      ),
    );
  };

  const removeSection = (index: number) => {
    onChange(sections.filter((_, i) => i !== index));
  };

  const addSection = () => {
    onChange([...sections, newSection(sections.length)]);
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Guide sections
      </Typography>

      {sections.map((section, index) => (
        <Paper key={section.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">Section {index + 1}</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                startIcon={<AutoFixHighIcon />}
                disabled={disabled || !ai}
                onClick={() =>
                  ai?.openEnhanceSection({
                    guideName,
                    section,
                    linkedApiUuids,
                    onAccept: (updated) => updateSection(index, updated),
                  })
                }
              >
                Enhance with AI
              </Button>
              <Button
                size="small"
                color="error"
                disabled={disabled}
                onClick={() => removeSection(index)}
              >
                Remove
              </Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Section title"
            margin="dense"
            disabled={disabled}
            value={section.title}
            onChange={(e) => updateSection(index, { title: e.target.value })}
          />
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Instructions (Markdown)"
            margin="normal"
            disabled={disabled}
            value={section.instructions}
            onChange={(e) =>
              updateSection(index, { instructions: e.target.value })
            }
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              mb: 1,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Code samples
            </Typography>
            <Button
              size="small"
              startIcon={<AutoFixHighIcon />}
              disabled={disabled || !ai}
              onClick={() =>
                ai?.openGenerateSamples({
                  guideName,
                  section,
                  linkedApiUuids,
                  onAccept: (samples) =>
                    updateSection(index, { codeSamples: samples }),
                })
              }
            >
              Generate samples
            </Button>
          </Box>

          {section.codeSamples.map((sample, sampleIndex) => (
            <DeveloperGuideCodeSampleEditor
              key={sample.id}
              sample={sample}
              disabled={disabled}
              onChange={(updated) => {
                const codeSamples = section.codeSamples.map((s, i) =>
                  i === sampleIndex ? updated : s,
                );
                updateSection(index, { codeSamples });
              }}
              onRemove={() => {
                updateSection(index, {
                  codeSamples: section.codeSamples.filter(
                    (_, i) => i !== sampleIndex,
                  ),
                });
              }}
            />
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={() =>
              updateSection(index, {
                codeSamples: [
                  ...section.codeSamples,
                  {
                    id: crypto.randomUUID(),
                    language: "curl",
                    label: "",
                    code: "",
                  },
                ],
              })
            }
          >
            Add code sample
          </Button>
        </Paper>
      ))}

      <Button startIcon={<AddIcon />} onClick={addSection} disabled={disabled}>
        Add section
      </Button>
    </Box>
  );
};

export default DeveloperGuideSectionEditor;
